import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";

export interface SimulatedTask {
  id: number;
  title: string;
  description: string;
  industry: string;
  difficulty: string;
  estimated_hours: number;
  skills_required: string;
  resources: string;
  deliverables: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface SimulatedWorkLog {
  id?: number;
  student_id?: number;
  task_id: number;
  date: string;
  hours_logged: number;
  activity: string;
  learning: string;
  challenge: string;
  evidence_url?: string;
  status?: string;
  feedback?: string;
}

// Get all simulated tasks
export function useSimulatedTasks(industry?: string) {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["simulated-tasks", industry],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const params = industry ? { industry } : {};
        const { data } = await apiClient.get("/simulated-tasks", { params });
        return (data as any)?.data || data;
      } catch {
        return [];
      }
    },
  });
}

// Get single task with work logs
export function useSimulatedTask(id: number) {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["simulated-task", id],
    queryFn: async () => {
      if (user?.isDemo) return null;
      try {
        const { data } = await apiClient.get(`/simulated-tasks/${id}`);
        return (data as any)?.data || data;
      } catch {
        return null;
      }
    },
    enabled: !!id,
  });
}

// Submit work log
export function useSubmitWorkLog() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (log: SimulatedWorkLog) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), ...log };
      }
      const { data } = await apiClient.post("/simulated-worklogs", log);
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulated-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["simulated-worklogs"] });
    },
  });
}

// Get my work logs
export function useMyWorkLogs() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["simulated-worklogs", user?.id],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const { data } = await apiClient.get("/simulated-worklogs/my");
        return (data as any)?.data || data;
      } catch {
        return [];
      }
    },
  });
}

// Complete task
export function useCompleteTask() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async ({ taskId, totalHours }: { taskId: number; totalHours: number }) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), task_id: taskId, total_hours: totalHours };
      }
      const { data } = await apiClient.post(`/simulated-tasks/${taskId}/complete`, {
        total_hours: totalHours,
      });
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulated-tasks"] });
    },
  });
}

// Get my completions
export function useMyCompletions() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["simulated-completions"],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const { data } = await apiClient.get("/simulated-completions/my");
        return (data as any)?.data || data;
      } catch {
        return [];
      }
    },
  });
}

// Stats for simulated work
export function useSimulatedStats() {
  const { data: tasks } = useSimulatedTasks();
  const { data: completions } = useMyCompletions();

  const tasksArray = (tasks as SimulatedTask[]) || [];
  const completionsArray = (completions as any[]) || [];

  return {
    totalTasks: tasksArray.length,
    completedTasks: completionsArray.length,
    totalHours: completionsArray.reduce((sum: number, c: any) => sum + (c.total_hours || 0), 0),
  };
}
